/**
 * OfflineBanner Component
 *
 * Animated banner shown when the device loses network connectivity.
 * Auto-hides with a brief "Back online" confirmation on reconnect.
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { useHaptic } from "../hooks/useHaptic";

export function OfflineBanner() {
  const { isOnline } = useNetworkStatus();
  const haptic = useHaptic();
  const { t } = useTranslation();
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      haptic.notification("error");
    } else if (wasOffline) {
      setShowReconnected(true);
      haptic.notification("success");
      const timer = setTimeout(() => {
        setShowReconnected(false);
        setWasOffline(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AnimatePresence>
      {!isOnline && (
        <motion.div
          key="offline"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="overflow-hidden"
        >
          <div className="bg-red-500 text-white text-center text-sm font-medium py-2 px-4" role="alert" aria-live="assertive">
            <span className="inline-flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M8.464 15.536a5 5 0 010-7.072" />
                <circle cx="12" cy="12" r="1" fill="currentColor" />
              </svg>
              {t("offlineBanner.offline")}
            </span>
          </div>
        </motion.div>
      )}
      {showReconnected && isOnline && (
        <motion.div
          key="reconnected"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="overflow-hidden"
        >
          <div className="bg-green-500 text-white text-center text-sm font-medium py-2 px-4" role="status" aria-live="polite">
            {t("offlineBanner.backOnline")}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
